import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";
import { toast } from "react-toastify";
import {
  KoraxPageHeader,
  KoraxButton
} from "../../components/KoraxUI";

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    width: "100%",
    height: "100%",
    overflow: "auto",
    background: theme.palette.type === "dark" ? "#080E1C" : "#F8FAFC",
    fontFamily: "'Inter', system-ui, sans-serif",
    ...theme.scrollbarStyles
  },
  inner: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    margin: "0",
    padding: "16px 18px 40px",
    boxSizing: "border-box",

    display: "flex",
    flexDirection: "column",
    gap: 14,

    [theme.breakpoints.down("sm")]: {
      padding: "12px 10px 28px",
      gap: 12
    }
  },
  hero: {
    borderRadius: 22,
    padding: 22,
    background: "linear-gradient(135deg, #0F3CC9 0%, #007BFF 55%, #00A3FF 100%)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 22px 50px rgba(0,102,255,.22)"
  },
  title: { fontSize: 28, fontWeight: 900, letterSpacing: -0.8, margin: 0 },
  sub: { fontSize: 14, opacity: .9, marginTop: 6, maxWidth: 760 },
  btn: {
    border: "none",
    borderRadius: 12,
    padding: "11px 18px",
    fontWeight: 800,
    cursor: "pointer",
    background: "#0F172A",
    color: "#fff",
    boxShadow: "0 12px 26px rgba(15,23,42,.18)"
  },
  btnLight: {
    border: "1px solid rgba(255,255,255,.55)",
    borderRadius: 12,
    padding: "11px 18px",
    fontWeight: 800,
    cursor: "pointer",
    background: "rgba(255,255,255,.16)",
    color: "#fff"
  },
  grid: {
    minWidth: 0,

    display: "grid",
    gridTemplateColumns: "minmax(0,1.2fr) minmax(320px,.8fr)",
    gap: 14,
    alignItems: "start",

    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr"
    }
  },
  card: {
    minWidth: 0,
    boxSizing: "border-box",

    padding: 16,

    background:
      theme.palette.type === "dark"
        ? "#0D1626"
        : "#FFFFFF",

    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.08)"
        : "#E2E8F0"
    }`,

    borderRadius: 16,

    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 22px rgba(0,0,0,.16)"
        : "0 7px 22px rgba(15,23,42,.05)"
  },
  cardTitle: {
    marginBottom: 4,

    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A",

    fontSize: 14,
    fontWeight: 750,
    lineHeight: 1.3
  },
  cardSub: {
    marginBottom: 14,

    color: "#64748B",
    fontSize: 11.5,
    lineHeight: 1.4
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.1)" : "#DDE6F2"}`,
    borderRadius: 12,
    padding: "12px 13px",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.05)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#0F172A",
    outline: "none",
    fontSize: 14
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.1)" : "#DDE6F2"}`,
    borderRadius: 12,
    padding: "12px 13px",
    background: theme.palette.type === "dark" ? "rgba(255,255,255,.05)" : "#F8FAFC",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#0F172A",
    outline: "none",
    fontSize: 14,
    minHeight: 86,
    resize: "vertical",
    fontFamily: "inherit"
  },
  label: { display: "block", fontSize: 11, fontWeight: 900, color: "#94A3B8", textTransform: "uppercase", letterSpacing: .7, marginBottom: 7 },
  step: {
    position: "relative",
    borderRadius: 18,
    border: "1px solid #DBEAFE",
    background: "linear-gradient(180deg, #F3F7FF 0%, #FFFFFF 100%)",
    padding: 18,
    marginBottom: 14
  },
  stepHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  stepNumber: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: "linear-gradient(135deg, #2563EB, #00A3FF)",
    color: "#fff",
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  timelineLine: {
    position: "absolute",
    left: 34,
    top: 58,
    bottom: -19,
    width: 2,
    background: "#DBEAFE"
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800
  },
  seqCard: {
    borderRadius: 18,
    border: "1px solid #E5EAF3",
    background: "#fff",
    padding: 18,
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 12
  },
  seqIcon: {
    width: 44,
    height: 44,
    borderRadius: 15,
    background: "linear-gradient(135deg, #2563EB, #00A3FF)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    boxShadow: "0 14px 24px rgba(37,99,235,.18)"
  },
  smallBtn: {
    border: "1px solid #BFDBFE",
    background: "#EFF6FF",
    color: "#1D4ED8",
    borderRadius: 10,
    padding: "8px 11px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer"
  },
  dangerBtn: {
    border: "1px solid rgba(239,68,68,.25)",
    background: "#FEF2F2",
    color: "#DC2626",
    borderRadius: 10,
    padding: "8px 11px",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer"
  },
  template: {
    border: "1px solid #E5EAF3",
    borderRadius: 15,
    padding: 14,
    cursor: "pointer",
    background: "#fff",
    marginBottom: 10,
    transition: ".18s",
    "&:hover": { transform: "translateY(-2px)", borderColor: "#93C5FD", boxShadow: "0 12px 28px rgba(37,99,235,.1)" }
  }
}));

const EMPTY_STEP = { body: "", delaySeconds: 30, mediaKey: "", mediaType: "" };
const EMPTY_SEQ = { name: "", description: "", steps: [{ ...EMPTY_STEP }] };

const TYPE_ICONS = { image: "🖼️", audio: "🎙️", video: "🎬", pdf: "📄" };

const TEMPLATES = [
  {
    name: "Follow-up pós orçamento",
    description: "Sequência para recuperar lead que recebeu preço e não respondeu.",
    steps: [
      { body: "Oi {{nome}}, tudo bem? Conseguiu ver a proposta que te mandei?", delaySeconds: 10, mediaKey: "", mediaType: "" },
      { body: "Se quiser, posso te ajudar a escolher a melhor opção agora.", delaySeconds: 3600, mediaKey: "", mediaType: "" }
    ]
  },
  {
    name: "Pós reunião",
    description: "Sequência curta para manter contato depois da reunião.",
    steps: [
      { body: "Obrigado pelo tempo hoje, {{nome}}. Vou deixar tudo organizado por aqui e te chamo com o próximo passo.", delaySeconds: 10, mediaKey: "", mediaType: "" },
      { body: "Passando só para saber se ficou alguma dúvida sobre o que conversamos.", delaySeconds: 86400, mediaKey: "", mediaType: "" }
    ]
  },
  {
    name: "Reativação de lead",
    description: "Para leads antigos que pararam de responder.",
    steps: [
      { body: "Oi {{nome}}, tudo bem? Vi seu contato aqui e queria saber se ainda faz sentido retomarmos.", delaySeconds: 10, mediaKey: "", mediaType: "" },
      { body: "Posso te mandar uma condição atualizada?", delaySeconds: 7200, mediaKey: "", mediaType: "" }
    ]
  }
];

const getSteps = seq => seq?.steps || seq?.MessageSequenceSteps || [];
const getTotalDelay = seq => getSteps(seq).reduce((acc, step) => acc + Number(step.delaySeconds || 0), 0);
const getMediaCount = seq => getSteps(seq).filter(step => step.mediaKey).length;
const getTextCount = seq => getSteps(seq).filter(step => String(step.body || "").trim()).length;

export default function MessageSequences() {
  const classes = useStyles();
  const [sequences, setSequences] = useState([]);
  const [medias, setMedias] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SEQ);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/message-sequences").then(r => setSequences(r.data || [])).catch(() => {});
    api.get("/cavia-medias").then(r => setMedias(r.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const activeMedias = medias.filter(m => m.isActive);

  const formatDelay = seconds => {
    const s = Number(seconds || 0);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.round(s / 60)}min`;
    if (s < 86400) return `${Math.round(s / 3600)}h`;
    return `${Math.round(s / 86400)}d`;
  };

  const handleNew = () => {
    setForm({ ...EMPTY_SEQ, steps: [{ ...EMPTY_STEP }] });
    setEditing("new");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTemplate = tpl => {
    setForm(JSON.parse(JSON.stringify(tpl)));
    setEditing("new");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEdit = seq => {
    const sourceSteps = getSteps(seq);
    setForm({
      name: seq.name || "",
      description: seq.description || "",
      steps: sourceSteps.length
        ? sourceSteps.map(s => ({
            body: s.body || "",
            delaySeconds: Number(s.delaySeconds || 30),
            mediaKey: s.mediaKey || "",
            mediaType: s.mediaType || ""
          }))
        : [{ ...EMPTY_STEP }]
    });
    setEditing(seq.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDuplicate = async seq => {
    const copy = {
      name: `${seq.name} - cópia`,
      description: seq.description || "",
      steps: getSteps(seq).map(s => ({
        body: s.body || "",
        delaySeconds: Number(s.delaySeconds || 30),
        mediaKey: s.mediaKey || "",
        mediaType: s.mediaType || ""
      }))
    };
    await api.post("/message-sequences", copy);
    toast.success("Sequência duplicada!");
    load();
  };

  const handleToggle = async seq => {
    await api.put(`/message-sequences/${seq.id}`, {
      name: seq.name,
      description: seq.description,
      isActive: !seq.isActive,
      steps: getSteps(seq)
    });
    toast.success(seq.isActive ? "Sequência pausada" : "Sequência ativada");
    load();
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.warning("Informe o nome da sequência");
    if (!form.steps.length) return toast.warning("Adicione pelo menos uma etapa");

    setLoading(true);
    try {
      const payload = {
        ...form,
        steps: form.steps.map(s => ({
          body: s.body || "",
          delaySeconds: Number(s.delaySeconds || 1),
          mediaKey: s.mediaKey || null,
          mediaType: s.mediaType || null
        }))
      };

      if (editing === "new") {
        await api.post("/message-sequences", payload);
        toast.success("✅ Sequência criada!");
      } else {
        await api.put(`/message-sequences/${editing}`, payload);
        toast.success("✅ Sequência atualizada!");
      }

      setEditing(null);
      setForm({ ...EMPTY_SEQ, steps: [{ ...EMPTY_STEP }] });
      load();
    } catch {
      toast.error("Erro ao salvar");
    }
    setLoading(false);
  };

  const handleDelete = async id => {
    if (!window.confirm("Excluir esta sequência?")) return;
    await api.delete(`/message-sequences/${id}`);
    toast.success("Sequência removida");
    load();
  };

  const addStep = () => setForm(f => ({ ...f, steps: [...f.steps, { ...EMPTY_STEP }] }));
  const removeStep = i => setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  const updateStep = (i, field, value) =>
    setForm(f => {
      const steps = [...f.steps];
      steps[i] = { ...steps[i], [field]: value };
      return { ...f, steps };
    });

  return (
    <div className={classes.root}>
      <div className={classes.inner}>
        <KoraxPageHeader
          eyebrow="Automação"
          icon={<span style={{ fontSize: 20 }}>⚡</span>}
          title="Sequências"
          subtitle="Crie fluxos automáticos com texto, áudio, mídia e intervalos personalizados."
          actions={
            <>
              <KoraxButton
                variant="secondary"
                onClick={() =>
                  window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: "smooth"
                  })
                }
              >
                Modelos prontos
              </KoraxButton>

              <KoraxButton
                variant="primary"
                onClick={handleNew}
              >
                + Nova sequência
              </KoraxButton>
            </>
          }
        />

        {editing && (
          <section className={classes.card}>
            <div className={classes.cardTitle}>{editing === "new" ? "Nova sequência" : "Editar sequência"}</div>
            <div className={classes.cardSub}>Monte uma linha do tempo com os passos que serão enviados ao lead.</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
              <div>
                <label className={classes.label}>Nome da sequência *</label>
                <input className={classes.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Follow-up pós orçamento" />
              </div>
              <div>
                <label className={classes.label}>Descrição</label>
                <input className={classes.input} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Para que serve esta sequência?" />
              </div>
            </div>

            {form.steps.map((step, i) => (
              <div className={classes.step} key={i}>
                {i < form.steps.length - 1 && <div className={classes.timelineLine} />}
                <div className={classes.stepHead}>
                  <div className={classes.stepNumber}>{i + 1}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 900 }}>Etapa {i + 1}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>Aguardar {formatDelay(step.delaySeconds)} antes de enviar</div>
                  </div>
                  <input type="number" min="1" className={classes.input} style={{ width: 92 }} value={step.delaySeconds} onChange={e => updateStep(i, "delaySeconds", Number(e.target.value))} />
                  <span style={{ fontSize: 12, color: "#64748B" }}>seg</span>
                  {form.steps.length > 1 && <button className={classes.dangerBtn} onClick={() => removeStep(i)}>Remover</button>}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                  <div>
                    <label className={classes.label}>Mídia da biblioteca</label>
                    <select
                      className={classes.input}
                      value={step.mediaKey || ""}
                      onChange={e => {
                        const selected = activeMedias.find(m => m.mediaKey === e.target.value);
                        updateStep(i, "mediaKey", e.target.value);
                        updateStep(i, "mediaType", selected?.type || "");
                      }}
                    >
                      <option value="">— Sem mídia —</option>
                      {activeMedias.map(m => (
                        <option key={m.id} value={m.mediaKey}>
                          {TYPE_ICONS[m.type] || "📎"} {m.name} ({m.mediaKey})
                        </option>
                      ))}
                    </select>
                    {step.mediaKey && (
                      <div style={{ marginTop: 8 }}>
                        <span className={classes.badge} style={{ background: "#ECFDF5", color: "#16A34A" }}>
                          ✅ Mídia selecionada: {step.mediaKey}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={classes.label}>Mensagem de texto</label>
                    <textarea className={classes.textarea} value={step.body || ""} onChange={e => updateStep(i, "body", e.target.value)} placeholder="Olá {{nome}}, tudo bem?" />
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>
                      Use <strong>{"{{nome}}"}</strong> para chamar o lead pelo nome.
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
              <button className={classes.smallBtn} onClick={addStep}>+ Adicionar etapa</button>
              <div style={{ display: "flex", gap: 10 }}>
                <button className={classes.smallBtn} onClick={() => setEditing(null)}>Cancelar</button>
                <button className={classes.btn} onClick={handleSave} disabled={loading}>{loading ? "Salvando..." : "Salvar sequência"}</button>
              </div>
            </div>
          </section>
        )}

        <div className={classes.grid}>
          <section className={classes.card}>
            <div className={classes.cardTitle}>Sequências cadastradas</div>
            <div className={classes.cardSub}>{sequences.length} fluxo(s) disponível(is)</div>

            {sequences.length === 0 ? (
              <div style={{ color: "#64748B", textAlign: "center", padding: 26 }}>Nenhuma sequência cadastrada ainda.</div>
            ) : (
              sequences.map(seq => (
                <div className={classes.seqCard} key={seq.id}>
                  <div className={classes.seqIcon}>⚡</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 900, fontSize: 16 }}>{seq.name}</div>
                    {seq.description && <div style={{ fontSize: 13, color: "#64748B", marginTop: 3 }}>{seq.description}</div>}
                    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
                      <span className={classes.badge} style={{ background: "#EFF6FF", color: "#1D4ED8" }}>⚡ {getSteps(seq).length} etapa(s)</span>
                      <span className={classes.badge} style={{ background: "#F1F5F9", color: "#64748B" }}>⏱ {formatDelay(getTotalDelay(seq))}</span>
                      {getTextCount(seq) > 0 && <span className={classes.badge} style={{ background: "#E0F2FE", color: "#0284C7" }}>💬 {getTextCount(seq)} texto(s)</span>}
                      {getMediaCount(seq) > 0 && <span className={classes.badge} style={{ background: "#F5F3FF", color: "#7C3AED" }}>🎙️ {getMediaCount(seq)} mídia(s)</span>}
                      <span className={classes.badge} style={{ background: seq.isActive ? "#ECFDF5" : "#FEF2F2", color: seq.isActive ? "#16A34A" : "#DC2626" }}>
                        {seq.isActive ? "🟢 Ativa" : "🔴 Pausada"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 13 }}>
                      <button className={classes.smallBtn} onClick={() => handleEdit(seq)}>Editar</button>
                      <button className={classes.smallBtn} onClick={() => handleDuplicate(seq)}>Duplicar</button>
                      <button className={classes.smallBtn} onClick={() => handleToggle(seq)}>{seq.isActive ? "Pausar" : "Ativar"}</button>
                      <button className={classes.dangerBtn} onClick={() => handleDelete(seq.id)}>Excluir</button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </section>

          <aside className={classes.card}>
            <div className={classes.cardTitle}>Modelos prontos</div>
            <div className={classes.cardSub}>Comece com uma estrutura pronta e ajuste em segundos.</div>
            {TEMPLATES.map((tpl, i) => (
              <div className={classes.template} key={i} onClick={() => handleTemplate(tpl)}>
                <div style={{ fontWeight: 900, color: "#0F172A" }}>⚡ {tpl.name}</div>
                <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>{tpl.description}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  <span className={classes.badge} style={{ background: "#EFF6FF", color: "#1D4ED8" }}>{tpl.steps.length} etapas</span>
                  <span className={classes.badge} style={{ background: "#F1F5F9", color: "#64748B" }}>{formatDelay(getTotalDelay(tpl))}</span>
                </div>
              </div>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
}
